import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye } from 'lucide-react';
import {
  Button,
  Table,
  Modal,
  Pagination,
  SearchBar,
  Loading,
  Alert,
} from '../../components/common';
import UserForm from '../../components/usuarios/UserForm';
import UserDetail from '../../components/usuarios/UserDetail';
import { usuariosAPI } from '../../api';
import useAuthStore from '../../store/authStore';

const UsuariosPage = () => {
  const { user: currentUser } = useAuthStore();

  // Estado
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Búsqueda y filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    rol: '',
    activo: '',
    id_organizacion: '',
  });

  // Ordenamiento
  const [sortColumn, setSortColumn] = useState('fecha_creacion');
  const [sortDirection, setSortDirection] = useState('desc');

  // Modales
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);

  // Cargar usuarios
  useEffect(() => {
    loadUsuarios();
  }, [currentPage, itemsPerPage, searchTerm, filters, sortColumn, sortDirection]);

  const loadUsuarios = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage,
      };

      // Agregar filtros si existen
      if (searchTerm) params.buscar = searchTerm;
      if (filters.rol) params.rol = filters.rol;
      if (filters.activo !== '') params.activo = filters.activo === 'true';
      if (filters.id_organizacion) params.id_organizacion = parseInt(filters.id_organizacion);

      const response = await usuariosAPI.getAll(params);
      const data = response.data;

      setUsuarios(data.items || []);
      setTotalItems(data.total || 0);
      setTotalPages(Math.ceil((data.total || 0) / itemsPerPage));
    } catch (err) {
      setError(err.message || 'Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  // Handlers
  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleFilterChange = (key, value) => {
    setFilters({
      ...filters,
      [key]: value,
    });
    setCurrentPage(1);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleCreate = async (userData) => {
    try {
      setFormLoading(true);
      setFormError(null);

      await usuariosAPI.create(userData);

      setSuccess('Usuario creado exitosamente');
      setShowCreateModal(false);
      loadUsuarios();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setFormError(err.message || 'Error al crear usuario');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = async (userData) => {
    try {
      setFormLoading(true);
      setFormError(null);

      await usuariosAPI.update(selectedUser.id_usuario, userData);

      setSuccess('Usuario actualizado exitosamente');
      setShowEditModal(false);
      setSelectedUser(null);
      loadUsuarios();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setFormError(err.message || 'Error al actualizar usuario');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setFormLoading(true);
      setFormError(null);

      await usuariosAPI.delete(selectedUser.id_usuario);

      setSuccess('Usuario eliminado exitosamente');
      setShowDeleteModal(false);
      setSelectedUser(null);
      loadUsuarios();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setFormError(err.message || 'Error al eliminar usuario');
    } finally {
      setFormLoading(false);
    }
  };

  // Definir columnas de la tabla
  const columns = [
    {
      key: 'nombre_completo',
      label: 'Nombre',
      sortable: true,
      render: (value, user) => (
        <div className="flex items-center">
          <div className="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center mr-3">
            <span className="text-sm font-medium text-white">
              {value?.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <div className="font-medium text-gray-900">{value}</div>
            <div className="text-sm text-gray-500">{user.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'rol',
      label: 'Rol',
      sortable: true,
      render: (value) => {
        const badges = {
          superadmin: 'bg-purple-100 text-purple-800',
          admin_org: 'bg-blue-100 text-blue-800',
          miembro: 'bg-gray-100 text-gray-800',
        };
        const labels = {
          superadmin: 'Super Admin',
          admin_org: 'Admin Org',
          miembro: 'Miembro',
        };
        return (
          <span
            className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
              badges[value] || 'bg-gray-100 text-gray-800'
            }`}
          >
            {labels[value] || value}
          </span>
        );
      },
    },
    {
      key: 'nombre_organizacion',
      label: 'Organización',
      render: (value) => value || 'Sin organización',
    },
    {
      key: 'activo',
      label: 'Estado',
      sortable: true,
      render: (value) => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
            value
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {value ? 'Activo' : 'Inactivo'}
        </span>
      ),
    },
    {
      key: 'acciones',
      label: 'Acciones',
      render: (_, user) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setSelectedUser(user);
              setShowDetailModal(true);
            }}
            className="text-blue-600 hover:text-blue-900"
            title="Ver detalle"
          >
            <Eye className="h-4 w-4" />
          </button>
          <button
            onClick={() => {
              setSelectedUser(user);
              setShowEditModal(true);
            }}
            className="text-yellow-600 hover:text-yellow-900"
            title="Editar"
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            onClick={() => {
              setSelectedUser(user);
              setShowDeleteModal(true);
            }}
            className="text-red-600 hover:text-red-900"
            title="Eliminar"
            disabled={user.id_usuario === currentUser?.id_usuario}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  // Configuración de filtros
  const filterConfig = [
    {
      key: 'rol',
      label: 'Rol',
      type: 'select',
      value: filters.rol,
      options: [
        { value: 'superadmin', label: 'Super Administrador' },
        { value: 'admin_org', label: 'Admin Organización' },
        { value: 'miembro', label: 'Miembro' },
      ],
    },
    {
      key: 'activo',
      label: 'Estado',
      type: 'select',
      value: filters.activo,
      options: [
        { value: 'true', label: 'Activo' },
        { value: 'false', label: 'Inactivo' },
      ],
    },
    {
      key: 'id_organizacion',
      label: 'ID Organización',
      type: 'text',
      value: filters.id_organizacion,
      placeholder: 'Buscar por ID...',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Usuarios</h1>
          <p className="mt-2 text-gray-600">
            Gestiona los usuarios del sistema
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Nuevo Usuario
        </Button>
      </div>

      {/* Alertas */}
      {success && (
        <Alert type="success" message={success} onClose={() => setSuccess(null)} />
      )}
      {error && (
        <Alert type="error" message={error} onClose={() => setError(null)} />
      )}

      {/* Búsqueda y filtros */}
      <SearchBar
        placeholder="Buscar por nombre o email..."
        onSearch={handleSearch}
        filters={filterConfig}
        onFilterChange={handleFilterChange}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
      />

      {/* Tabla */}
      <Table
        columns={columns}
        data={usuarios}
        onSort={handleSort}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        loading={loading}
        emptyMessage="No se encontraron usuarios"
      />

      {/* Paginación */}
      {!loading && totalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={(value) => {
            setItemsPerPage(value);
            setCurrentPage(1);
          }}
        />
      )}

      {/* Modal Crear */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setFormError(null);
        }}
        title="Crear Nuevo Usuario"
        size="lg"
      >
        <UserForm
          onSubmit={handleCreate}
          onCancel={() => {
            setShowCreateModal(false);
            setFormError(null);
          }}
          loading={formLoading}
          error={formError}
        />
      </Modal>

      {/* Modal Editar */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedUser(null);
          setFormError(null);
        }}
        title="Editar Usuario"
        size="lg"
      >
        <UserForm
          user={selectedUser}
          onSubmit={handleEdit}
          onCancel={() => {
            setShowEditModal(false);
            setSelectedUser(null);
            setFormError(null);
          }}
          loading={formLoading}
          error={formError}
        />
      </Modal>

      {/* Modal Detalle */}
      <Modal
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedUser(null);
        }}
        title="Detalle del Usuario"
        size="lg"
        footer={
          <Button onClick={() => setShowDetailModal(false)}>
            Cerrar
          </Button>
        }
      >
        {selectedUser && <UserDetail user={selectedUser} />}
      </Modal>

      {/* Modal Eliminar */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedUser(null);
          setFormError(null);
        }}
        title="Confirmar Eliminación"
        size="sm"
      >
        <div className="space-y-4">
          {formError && <Alert type="error" message={formError} />}
          
          <p className="text-gray-700">
            ¿Estás seguro de que deseas eliminar al usuario{' '}
            <strong>{selectedUser?.nombre_completo}</strong>?
          </p>
          <p className="text-sm text-gray-600">
            Esta acción desactivará el usuario pero mantendrá su registro en el sistema.
          </p>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => {
                setShowDeleteModal(false);
                setSelectedUser(null);
                setFormError(null);
              }}
              disabled={formLoading}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              loading={formLoading}
            >
              Eliminar Usuario
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default UsuariosPage;
